/**
 * Keeping a ticket's invoice on the ticket's own property — linking across the
 * two puts one client's work on another client's invoice (AMP-467). The API
 * refuses the mismatch; these keep the forms from offering it.
 */

export type LinkableInvoice = { id: string; property?: { id: string } | null };

export type InvoiceSuggestion = { id: string };

/** The invoices a ticket on `propertyId` may be linked to. Nothing is
 * selectable without a property (both forms disable the field until one is
 * picked), and an invoice whose own property was deleted can't be matched. */
export function invoicesForProperty<T extends LinkableInvoice>(
  invoices: T[] | undefined,
  propertyId: string | null | undefined,
): T[] {
  if (!propertyId || !invoices) return [];
  return invoices.filter((invoice) => invoice.property?.id === propertyId);
}

/** Whether a chosen invoice is no longer among the ones this property offers. */
export function isInvoiceSelectionStale(
  selectedInvoiceId: string | null | undefined,
  selectableInvoices: LinkableInvoice[],
): boolean {
  if (!selectedInvoiceId) return false;
  return !selectableInvoices.some((invoice) => invoice.id === selectedInvoiceId);
}

/** AI-suggested invoices, minus any on another property. Suggestions render
 * ahead of the filtered options and are matched on invoice number alone, so a
 * same-numbered invoice at another client can otherwise lead the list. */
export function suggestionsForProperty<S extends InvoiceSuggestion>(
  suggestions: S[] | undefined,
  selectableInvoices: LinkableInvoice[],
): S[] | undefined {
  if (!suggestions) return undefined;
  const selectableIds = new Set(selectableInvoices.map((invoice) => invoice.id));
  return suggestions.filter((suggestion) => selectableIds.has(suggestion.id));
}

/** The suffix marking an invoice added back in by {@link withOutOfScopeInvoice}
 * that this reviewer CAN read — its number is real and safe to show. */
const OUT_OF_SCOPE_INVOICE_SUFFIX = ' — another property';

/** The label for an invoice added back in by {@link withOutOfScopeInvoice}
 * that this reviewer CANNOT read: `allInvoices` (`GET /invoices/?variant=form`)
 * is tenant-filtered server-side, so an id resolved onto another tenant's
 * invoice never appears there and there is no number available to show. */
export const UNREADABLE_INVOICE_LABEL = 'Invoice from another client — clear to continue';

export type ScopedInvoiceOption = {
  id: string;
  number?: string | null;
  outOfScope?: boolean;
  unreadable?: boolean;
};

/** Appends the selected invoice back into the option list when the property
 * filter has excluded it, so it renders as a visible, clearable value instead
 * of a blank field the reviewer can't act on. The extraction agent resolves
 * `invoiceId` on its own (AMP-610's root cause), and that mismatch gets
 * neither an option nor a suggestion.
 *
 * Two distinct cases, because `allInvoices` (`GET /invoices/?variant=form`,
 * `viewset.py:640`) is itself tenant-filtered server-side: an invoice on
 * another of the reviewer's OWN properties is still found there and flagged
 * `outOfScope` with its real number; an invoice on another tenant entirely is
 * absent from `allInvoices` too, so it's rendered as an id-only placeholder
 * flagged `unreadable` — there is no number this reviewer is allowed to see.
 * Without the second case, an id present in form state but absent from every
 * invoice this reviewer can read still renders blank (AMP-693).
 *
 * `allInvoices` itself being `undefined` (still loading, or the fetch failed)
 * is a THIRD state, not a variant of "unreadable": an id can't be judged
 * absent from a list that doesn't exist yet, so it renders unchanged
 * (blank while ambiguous, the same restraint `TicketExtractionReviewFields`
 * already applies to staleness checks) rather than accusing a correct,
 * still-loading link of being unreadable. */
export function withOutOfScopeInvoice<T extends LinkableInvoice & { number?: string | null }>(
  selectableInvoices: T[],
  allInvoices: T[] | undefined,
  selectedInvoiceId: string | null | undefined,
): Array<T | ScopedInvoiceOption> {
  if (
    !selectedInvoiceId ||
    selectableInvoices.some((invoice) => invoice.id === selectedInvoiceId)
  ) {
    return selectableInvoices;
  }
  const outOfScope = allInvoices?.find((invoice) => invoice.id === selectedInvoiceId);
  if (outOfScope) {
    return [...selectableInvoices, { ...outOfScope, outOfScope: true }];
  }
  // Absent from a list we have is "unreadable"; absent because we have no
  // list yet is just "not loaded" — don't accuse a link we can't yet judge
  // (AMP-467). Once allInvoices resolves, an id genuinely missing from it
  // falls through to the placeholder below.
  if (!allInvoices) return selectableInvoices;
  return [...selectableInvoices, { id: selectedInvoiceId, number: null, unreadable: true }];
}

/** The dropdown label for an invoice, marking one {@link withOutOfScopeInvoice}
 * added back in so the reviewer can tell it apart from an in-scope choice. */
export function invoiceOptionLabel(invoice: ScopedInvoiceOption): string {
  if (invoice.unreadable) return UNREADABLE_INVOICE_LABEL;
  const base = invoice.number || invoice.id;
  return invoice.outOfScope ? `${base}${OUT_OF_SCOPE_INVOICE_SUFFIX}` : base;
}

/** The label for a ticket in an invoice's "link a ticket" picker. A linked
 * ticket is disabled there; naming the invoice holding it turns a dead end into
 * something actionable. The number is missing when that invoice is outside what
 * this user can read — the state a cross-client link leaves behind. */
export function linkedTicketOptionLabel(
  ticketNumber: string,
  invoice: { id: string; number?: string | null } | null | undefined,
): string {
  if (!invoice) return ticketNumber;
  return invoice.number
    ? `${ticketNumber} (Already linked to ${invoice.number})`
    : `${ticketNumber} (Already linked)`;
}
